import {useEffect, useState} from 'react';

/**
 * Pixels of the layout viewport that are hidden behind the browser's bottom chrome.
 *
 * iOS Safari lays the page out against the *large* viewport — the size the page would have
 * if the toolbars were hidden — so `window.innerHeight` counts the strip of page sitting
 * behind the bottom toolbar. Anything pinned with `position: fixed; bottom: 0` is therefore
 * rendered underneath that toolbar and simply cannot be seen. `visualViewport` reports what
 * is actually on screen, and the difference is how far up a fixed element has to sit.
 *
 * Returns 0 everywhere that does not have this problem (desktop, Android Chrome, and iOS
 * once the toolbar has collapsed), so callers can add it unconditionally.
 *
 * Note this is *not* the same thing as `env(safe-area-inset-bottom)`, and the two do not
 * double-count: the safe-area inset covers the home indicator and reads ~0 while the
 * toolbar is up, which is exactly when this value is large.
 */
export default function useViewportBottomInset(): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;

    const update = () => {
      const layoutHeight = document.documentElement.clientHeight;
      // How much of the layout viewport falls below what the user can actually see.
      const hidden = layoutHeight - (vv.height + vv.offsetTop);
      // Clamp: pinch-zoom and keyboard states can make this negative or absurdly large.
      setInset(Math.max(0, Math.min(Math.round(hidden), Math.round(layoutHeight / 3))));
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  return inset;
}
